import {
  CadType,
  ContourDescriptor,
  IBaseCadItem,
  IBaseItem,
  IBaseMeasureItem,
  ICadCone,
  ICadCylinder,
  ICadFreeform,
  ICadModel,
  ICadPlane,
  ICadSphere,
  ICadTorus,
  ICadTruncatedCone,
  ICadUndefined,
  IDistanceMapMeasure,
  IDistanceMeasure,
  IGroupableItem,
  IGroupItem,
  IMeasureItem,
  IModelItem,
  IObjectItem,
  IOrderingItem,
  IPhotoSetItem,
  IPointCloudScanItem,
  IProjectContent,
  IScanItem,
  ISectionGroupMeasure,
  ISimpleScanItem,
  MeasureItemType,
  ObjectItemType,
  PhotoDescriptor,
  PhotoState,
  SectionDescriptor
} from "_src/client/project_content_types";

import {cadType, objectType, photoState, scanMode} from "_src/constants";
import appState from "_src/app_state";
import events from "_src/engine/events";

class BaseItem implements IBaseItem {
  id: string;
  uuid: string;
  name: string;
  visible: boolean;

  constructor(property: any) {
    this.id = property.getValue("id");
    this.uuid = property.getValue("uuid");
    this.name = property.getValue("name");
    this.visible = property.getValue("visible");
  }
}

class GroupableItem extends BaseItem implements IGroupableItem {
  groupID?: string;

  constructor(property: any) {
    super(property);

    if (property.getValue("groupID").length > 0) this.groupID = property.getValue("groupID");
  }
}

class GroupItem extends GroupableItem implements IGroupItem {
  type: ObjectItemType.Group;
  color: string;

  constructor(property: any) {
    super(property);
    this.type = ObjectItemType.Group;
    this.color = property.getValue("color");
  }
}

class ModelItem extends GroupableItem implements IModelItem {
  type: ObjectItemType.Model;

  color: string;
  polygons: number;
  vertices: number;
  scanner?: string;
  size: number;
  isHD: boolean;
  textured: boolean;

  constructor(property: any) {
    super(property);
    this.type = ObjectItemType.Model;

    this.color = property.getValue("color");
    this.polygons = property.getValue("numPolygons");
    this.vertices = property.getValue("numVertices");
    this.size = property.getValue("datasize");
    this.isHD = property.getValue("isHD");
    this.textured = Boolean(property.getValue("numTexturedFrames") > 0);

    if (property.getValue("scannertype").length > 0 && property.getValue("scannertype").toLowerCase() !== "unknown") this.scanner = property.getValue("scannertype");
  }
}

class ScanItem extends GroupableItem implements IScanItem {
  color: string;

  polygons: number;
  vertices: number;
  scanner?: string;
  frames: number;
  texturedFrames: number;
  size: number;

  isHD: boolean;
  hasrawgeometry: boolean;
  targetRawScan?: string;

  globalapplied: boolean;
  maxError?: number;

  constructor(property: any) {
    super(property);

    this.color = property.getValue("color");
    this.polygons = appState.getStateValue("scanMode") === scanMode.TRIANGLES ? property.getValue("numPolygons") : property.getValue("numSurfels");
    this.vertices = appState.getStateValue("scanMode") === scanMode.TRIANGLES ? property.getValue("numVertices") : property.getValue("numSurfels");
    this.size = property.getValue("datasize");

    this.frames = property.getValue("numFrames");
    this.texturedFrames = property.getValue("numTexturedFrames");
    this.isHD = property.getValue("isHD");
    this.globalapplied = property.getValue("globalapplied");
    this.hasrawgeometry = property.getValue("hasrawgeometry");
    if (this.hasrawgeometry) this.targetRawScan = property.getValue("targetRawScan");

    if (property.getValue("scannertype").length > 0 && property.getValue("scannertype").toLowerCase() !== "unknown") this.scanner = property.getValue("scannertype");
    if (this.globalapplied) this.maxError = property.getValue("maxError");
  }
}

class SimpleScanItem extends ScanItem implements ISimpleScanItem {
  type: ObjectItemType.Scan;
  constructor(property: any) {
    super(property);
    this.type = ObjectItemType.Scan;
  }
}

class PointCloudScanItem extends ScanItem implements IPointCloudScanItem {
  type: ObjectItemType.PointCloud;
  constructor(property: any) {
    super(property);
    this.type = ObjectItemType.PointCloud;
  }
}

class BaseCadItem extends GroupableItem implements IBaseCadItem {
  type: ObjectItemType.Cad;

  color: string;

  polygons: number;
  vertices: number;
  size: number;

  constructor(property: any) {
    super(property);
    this.type = ObjectItemType.Cad;

    this.color = property.getValue("color");
    this.polygons = property.getValue("numPolygons");
    this.vertices = property.getValue("numVertices");
    this.size = property.getValue("datasize");
  }
}

class CadModel extends BaseCadItem implements ICadModel {
  cadType: CadType.Model;
  facesCount: number;

  constructor(property: any) {
    super(property);
    this.cadType = CadType.Model;
    this.facesCount = property.getValue("cadProperties")["numfaces"];
  }
}

class CadCylinder extends BaseCadItem implements ICadCylinder {
  cadType: CadType.Cylinder;
  radius: number;
  height: number;

  constructor(property: any) {
    super(property);
    this.cadType = CadType.Cylinder;
    this.radius = property.getValue("cadProperties")["radius"];
    this.height = property.getValue("cadProperties")["height"];
  }
}

class CadCone extends BaseCadItem implements ICadCone {
  cadType: CadType.Cone;
  radius: number;
  height: number;
  angle: number;

  constructor(property: any) {
    super(property);
    this.cadType = CadType.Cone;
    this.radius = property.getValue("cadProperties")["radius"];
    this.height = property.getValue("cadProperties")["height"];
    this.angle = property.getValue("cadProperties")["angle"];
  }
}

class CadTruncatedCone extends BaseCadItem implements ICadTruncatedCone {
  cadType: CadType.TruncatedCone;
  smallRadius: number;
  largeRadius: number;
  height: number;
  angle: number;

  constructor(property: any) {
    super(property);
    this.cadType = CadType.TruncatedCone;
    this.smallRadius = property.getValue("cadProperties")["small-radius"];
    this.largeRadius = property.getValue("cadProperties")["large-radius"];
    this.height = property.getValue("cadProperties")["height"];
    this.angle = property.getValue("cadProperties")["angle"];
  }
}

class CadSphere extends BaseCadItem implements ICadSphere {
  cadType: CadType.Sphere;
  radius: number;

  constructor(property: any) {
    super(property);
    this.cadType = CadType.Sphere;
    this.radius = property.getValue("cadProperties")["radius"];
  }
}

class CadPlane extends BaseCadItem implements ICadPlane {
  cadType: CadType.Plane;
  constructor(property: any) {
    super(property);
    this.cadType = CadType.Plane;
  }
}

class CadTorus extends BaseCadItem implements ICadTorus {
  cadType: CadType.Torus;
  majorRadius: number;
  minorRadius: number;

  constructor(property: any) {
    super(property);
    this.cadType = CadType.Torus;
    this.majorRadius = property.getValue("cadProperties")["major-radius"];
    this.minorRadius = property.getValue("cadProperties")["minor-radius"];
  }
}

class CadFreeform extends BaseCadItem implements ICadFreeform {
  cadType: CadType.Freeform;
  uOrder: number;
  vOrder: number;
  uPointsCount: number;
  vPointsCount: number;

  constructor(property: any) {
    super(property);
    this.cadType = CadType.Freeform;
    this.uOrder = property.getValue("cadProperties")["u-order"];
    this.vOrder = property.getValue("cadProperties")["v-order"];
    this.uPointsCount = property.getValue("cadProperties")["u-numpoints"];
    this.vPointsCount = property.getValue("cadProperties")["v-numpoints"];
  }
}

class CadUndefined extends BaseCadItem implements ICadUndefined {
  cadType: CadType.Undefined;
  constructor(property: any) {
    super(property);
    this.cadType = CadType.Undefined;
  }
}

class PhotoSetItem extends GroupableItem implements IPhotoSetItem {
  type: ObjectItemType.PhotoSet;

  registered: boolean;
  registrationTarget?: string;
  maxError?: number;
  failedPhotos?: number;

  totalPhotos: number;
  totalSize: number;
  camera?: string;
  focalLength?: number

  photos?: PhotoDescriptor[];

  constructor(property: any, parentableProperties: any[]) {
    super(property);
    this.type = ObjectItemType.PhotoSet;

    const extra = property.getValue("properties");
    this.registered = property.getValue("registered");
    if (this.registered) {
      this.maxError = extra["maxregerror"] ? extra["maxregerror"] : -1;
      this.failedPhotos = extra["numfailedphotos"] ? extra["numfailedphotos"] : 0;
      const parentObjectProp = parentableProperties.find(prop => prop.getValue("uuid") === extra["target-fusion-uuid"]);
      if (parentObjectProp) this.registrationTarget = parentObjectProp.getValue("id");
    }

    this.totalPhotos = extra["numphotos"] ? extra["numphotos"] : 0;
    this.totalSize = extra["datasize"] ? extra["datasize"] : 0;
    if (extra["camera-model"] && extra["camera-model"].toLowerCase() !== "unknown") this.camera = extra["camera-model"];
    if (extra["focal-length"] && typeof(extra["focal-length"]) === "number") this.focalLength = extra["focal-length"];

    const photosData = property.getValue("photos");
    if (photosData) {
      this.photos = [];
      for (const photoData of photosData) {
        const photoDesc: PhotoDescriptor = {
          id: photoData["id"],
          name: photoData["name"],
          state: _photoStateToExternal(photoData["state"]),
          registered: photoData["properties"]["regerror"] !== undefined && photoData["properties"]["regerror"] >= 0,
          size: photoData["properties"]["datasize"] ? photoData["properties"]["datasize"] : 0,
        };
        if (photoDesc.registered) photoDesc.regError = photoData["properties"]["regerror"];
        if (photoData["properties"]["camera-model"]) photoDesc.camera = photoData["properties"]["camera-model"];
        if (photoData["properties"]["focal-length-mm"]) photoDesc.focalLength = photoData["properties"]["focal-length-mm"];

        this.photos.push(photoDesc);
      }
    }
  }
}

class BaseMeasureItem extends BaseItem implements IBaseMeasureItem {
  affiliation?: string[];

  constructor(property: any, parentableProperties: any[]) {
    super(property);

    const affiliation = [];
    for (const measuredObject of property.getValue("measuredObjects") || []) {
      const measuredObjectUUID = measuredObject["object-uuid"];
      const parentObjectProp = parentableProperties.find(prop => prop.getValue("uuid") === measuredObjectUUID);
      if (parentObjectProp) affiliation.push(parentObjectProp.getValue("id"));
    }
    if (affiliation.length) this.affiliation = affiliation;
  }
}

class DistanceMeasure extends BaseMeasureItem implements IDistanceMeasure {
  type: MeasureItemType.Distance;

  selected: boolean;
  value?: number;
  constructor(property: any, parentableProperties: any[]) {
    super(property, parentableProperties);
    this.type = MeasureItemType.Distance;

    this.selected = property.getValue("selected");
    const distance = property.getValue("properties")["value"];
    if (distance !== undefined) this.value = distance;
  }
}

class SectionGroupMeasure extends BaseMeasureItem implements ISectionGroupMeasure {
  type: MeasureItemType.SectionGroup;

  selected: boolean;
  planes: number;
  distance: number;
  direction: string;
  sections: SectionDescriptor[];

  constructor(property: any, parentableProperties: any[]) {
    super(property, parentableProperties);
    this.type = MeasureItemType.SectionGroup;

    this.selected = property.getValue("selected");
    this.planes = property.getValue("properties")["planes"];
    this.distance = property.getValue("properties")["distance"];
    this.direction = property.getValue("properties")["direction"];
    this.sections = [];

    for (const sectionProperty of property.getValue("geometries") || []) {
      const sectionDesc: SectionDescriptor = {
        visible: sectionProperty["visible"],
        selected: sectionProperty["selected"],
        contours: [],
      }
      if (sectionProperty["properties"]["volume"] !== undefined) sectionDesc.volume = sectionProperty["properties"]["volume"];
      if (sectionProperty["properties"]["surfaceArea"] !== undefined) sectionDesc.surfaceArea = sectionProperty["properties"]["surfaceArea"];
      this.sections.push(sectionDesc);

      for (const contourProperty of sectionProperty["contours"] || []) {
        const contourDesc: ContourDescriptor = {
          visible: contourProperty["visible"],
          selected: contourProperty["selected"],
        }
        if (contourProperty["properties"]["area"] !== undefined) contourDesc.area = contourProperty["properties"]["area"];
        if (contourProperty["properties"]["perimeter"] !== undefined) contourDesc.perimeter = contourProperty["properties"]["perimeter"];
        sectionDesc.contours.push(contourDesc);
      }
    }
  }
}

class DistanceMapMeasure extends BaseMeasureItem implements IDistanceMapMeasure {
  type: MeasureItemType.DistanceMap;

  reference?: string;
  searchDistance: number;
  RMSD: number;
  expRange: number;
  expValue: number;
  meanAbsDeviation: number;
  meanAbsDistance: number;
  constructor(property: any, parentableProperties: any[]) {
    super(property, parentableProperties);
    this.type = MeasureItemType.DistanceMap;

    const measuredObjects = property.getValue("measuredObjects");
    if (measuredObjects.length > 1) {
      const parentObjectProp = parentableProperties.find(prop => prop.getValue("uuid") === measuredObjects[1]["object-uuid"]);
      if (parentObjectProp) this.reference = parentObjectProp.getValue("id");
    }
    if (this.affiliation) this.affiliation.length = 1;

    this.searchDistance = property.getValue("searchDistance");
    this.RMSD = property.getValue("properties")["root-mean-square"];
    this.expRange = this.RMSD * 3;
    this.expValue = property.getValue("properties")["mean-signed-distance"];
    this.meanAbsDeviation = property.getValue("properties")["mean-abs-deviation"];
    this.meanAbsDistance = property.getValue("properties")["mean-abs-distance"];
  }
}

class ProjectContent implements IProjectContent {
  objects: IObjectItem[];
  measures: IMeasureItem[];
  ordering: IOrderingItem[];

  constructor() {
    this.objects = [];
    this.measures = [];
    this.ordering = [];
  }

  clone(): IProjectContent {
    return JSON.parse(JSON.stringify(this));
  }

  sync(projectTree: any) {
    this.objects.length = 0;
    this.measures.length = 0;
    this.ordering.length = 0;

    const groupProperties = projectTree.getPropertiesByType(objectType.GROUP);
    const modelProperties = projectTree.getPropertiesByType(objectType.FUSION);
    const scanProperties = projectTree.getPropertiesByType(objectType.SCAN);
    const pointCloudProperties = projectTree.getPropertiesByType(objectType.POINTCLOUD);
    const cadProperties = projectTree.getPropertiesByType(objectType.CAD);
    const photoGroupProperties = projectTree.getPropertiesByType(objectType.PHOTO_GROUP);
    const distanceMeasureProperties = projectTree.getPropertiesByType(objectType.LINEAR_MEASURE).concat(projectTree.getPropertiesByType(objectType.GEODESIC_MEASURE));
    const sectionGroupProperties = projectTree.getPropertiesByType(objectType.SECTION);
    const distanceMapProperties = projectTree.getPropertiesByType(objectType.DISTANCE_MAP);
    const ordering = projectTree.getOrdering();

    const parentableProperties = modelProperties.concat(scanProperties).concat(pointCloudProperties).concat(cadProperties);

    groupProperties.forEach((property: any) => this.objects.push(new GroupItem(property)));
    modelProperties.forEach((property: any) => this.objects.push(new ModelItem(property)));
    scanProperties.forEach((property: any) => this.objects.push(new SimpleScanItem(property)));
    pointCloudProperties.forEach((property: any) => this.objects.push(new PointCloudScanItem(property)));
    cadProperties.forEach((property: any) => this.objects.push(new (_getConstructorByCadType(property.getValue("cadType")))(property)));
    photoGroupProperties.forEach((property: any) => this.objects.push(new PhotoSetItem(property, parentableProperties)));
    distanceMeasureProperties.forEach((property: any) => this.measures.push(new DistanceMeasure(property, parentableProperties)));
    sectionGroupProperties.forEach((property: any) => this.measures.push(new SectionGroupMeasure(property, parentableProperties)));
    distanceMapProperties.forEach((property: any) => this.measures.push(new DistanceMapMeasure(property, parentableProperties)));
    ordering.forEach((orderingItem: any) => this.ordering.push({id: orderingItem.id}));
  }
}

function _getConstructorByCadType(internalType: any) {
  switch (internalType) {
    case cadType.MODEL:
      return CadModel;
    case cadType.CYLINDER:
      return CadCylinder;
    case cadType.CONE:
      return CadCone;
    case cadType.TRUNCATED_CONE:
      return CadTruncatedCone;
    case cadType.SPHERE:
      return CadSphere;
    case cadType.PLANE:
      return CadPlane;
    case cadType.TORUS:
      return CadTorus;
    case cadType.FREEFORM:
      return CadFreeform;
    default:
      return CadUndefined;
  }
}

function _photoStateToExternal(state: any): PhotoState {
  switch (state) {
    case photoState.DEFAULT:
      return PhotoState.Default;
    case photoState.HOVERED:
      return PhotoState.Hovered;
    case photoState.ACTIVE:
      return PhotoState.Active;
  }
}

const _projectContent = new ProjectContent();
export function syncProjectContent(projectTree: any): void {
  _projectContent.sync(projectTree);
  events.emit("projectContent", _projectContent);
}
